import { isObject } from './type-utils';

/**
 * Generates a pseudo-unique color based on a string.
 * The color will be the same every time (assuming the saturation & lightness inputs are consistent).
 *
 * @param str String to convert (will be used to vary the hue only)
 * @param saturation Adjust the color saturation, 0.0 = gray, 1.0 = full color, defaults to 0.8
 * @param lightness Adjust the overall lightness, 0.0 = black, 1.0 = white, defaults to 0.65
 * @returns {*}
 */
export function colorFromString(str, saturation = 0.8, lightness = 0.65) {
  let a = 1;
  for (let i = 0; i < str.length; i++) {
    a = (17 * (a + str.charCodeAt(i))) % 360;
  }
  const hue = a / 360;

  return hsl2hex({ h: hue, s: saturation, l: lightness });
}

/**
 * Converts a color from rgb to rgb-hex
 * @param r
 * @param g
 * @param b
 * @returns {string}
 */
export function rgb2hex({ r, g, b }) {
  function digits(n) {
    let m = Math.round(255 * n).toString(16);
    return m.length === 1 ? '0' + m : m;
  }

  return '#' + digits(r) + digits(g) + digits(b);
}

/**
 * Converts a string hex value to an RGB object;
 * @param hex
 * @returns {{r: Number, g: Number, b: Number}}
 */
export function hex2rgb(hex) {
  if (isObject(hex)) return hex;
  hex = hex[0] === '#' ? hex.slice(1) : hex;
  return {
    r: parseInt(hex.slice(0, 2), 16) / 255,
    g: parseInt(hex.slice(2, 4), 16) / 255,
    b: parseInt(hex.slice(4, 6), 16) / 255,
  };
}

/**
 * Converts a color from hsl to rgb-hex
 * @param h
 * @param s
 * @param l
 * @returns {string}
 */
export function hsl2hex({ h, s, l }) {
  if (s === 0) {
    return rgb2hex({ r: l, g: l, b: l });
  }
  let var2 = l < 0.5 ? l * (1 + s) : l + s - s * l;
  let var1 = 2 * l - var2;
  const hue2rgb = function (hue) {
    if (hue < 0) {
      hue += 1;
    }
    if (hue > 1) {
      hue -= 1;
    }
    if (6 * hue < 1) {
      return var1 + (var2 - var1) * 6 * hue;
    }
    if (2 * hue < 1) {
      return var2;
    }
    if (3 * hue < 2) {
      return var1 + (var2 - var1) * 6 * (2 / 3 - hue);
    }
    return var1;
  };
  return rgb2hex({
    r: hue2rgb(h + 1 / 3),
    g: hue2rgb(h),
    b: hue2rgb(h - 1 / 3),
  });
}

export function blend({ c1, c2, ratio = 0.5 }) {
  c1 = hex2rgb(c1);
  c2 = hex2rgb(c2);
  let c1Ratio = 1 - ratio;
  let c2Ratio = ratio;
  return {
    r: c1.r * c1Ratio + c2.r * c2Ratio,
    g: c1.g * c1Ratio + c2.g * c2Ratio,
    b: c1.b * c1Ratio + c2.b * c2Ratio,
  };
}

export function blend2hex({ c1, c2, ratio }) {
  return rgb2hex(blend({ c1, c2, ratio }));
}

export const COLOR_SUCCESS = '#4BB05D';
export const COLOR_WARNING = '#EEB854';
export const COLOR_DANGER = '#B63D4C';

export function lowerIsBetterColor(percent) {
  const parabolicValue = (percent * percent) / 100;
  const colors =
    parabolicValue <= 50
      ? { c1: COLOR_SUCCESS, c2: COLOR_WARNING, ratio: parabolicValue / 50 }
      : {
          c1: COLOR_WARNING,
          c2: COLOR_DANGER,
          ratio: (parabolicValue - 50) / 50,
        };
  return blend2hex(colors);
}
